import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  DATA_CHANGE_PROPOSAL_KIND,
  formatDataChangeProposalTitle,
  type ScoutDataChangeProposal,
  type ScoutDataChangeProposalOperation,
} from "./scoutDataChangeProposal";
import { ScoutDataChangeProposalCard } from "./ScoutDataChangeProposalCard";
import {
  useScoutProposalActionsDisabled,
  useScoutProposalHandlers,
  useScoutProposalSurfaceReadOnly,
  type ScoutProposalResponseStatus,
} from "./ScoutProposalContext";

type Props = {
  proposals: ScoutDataChangeProposal[];
};

type SubmittingAction = "confirm-all" | "decline-all" | "flush" | null;

type LocalDecision = {
  status: ScoutProposalResponseStatus;
  title: string;
  operation?: ScoutDataChangeProposalOperation;
};

type ResolveItem = {
  proposalId: string;
  title: string;
  status: ScoutProposalResponseStatus;
  operation?: ScoutDataChangeProposalOperation;
};

/**
 * Stack of staged data-change cards under a tool group.
 *
 * Per-card Accept/Reject decisions are held locally until every card in the
 * stack is decided (or Accept all / Reject all runs). Then one synthetic
 * continue message is sent — so mixed Accept-then-Reject never races a second
 * Scout send against an active turn.
 */
export function ScoutDataChangeProposalStack({ proposals }: Props) {
  const handlers = useScoutProposalHandlers(DATA_CHANGE_PROPOSAL_KIND);
  const surfaceReadOnly = useScoutProposalSurfaceReadOnly();
  const turnActionsDisabled = useScoutProposalActionsDisabled();
  const [hasError, setHasError] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<SubmittingAction>(null);
  const [localDecisions, setLocalDecisions] = useState<Record<string, LocalDecision>>({});
  // Ref mirrors localDecisions so rapid per-card clicks see each other before
  // the next React render commits (Accept then Reject on siblings).
  const localDecisionsRef = useRef(localDecisions);
  localDecisionsRef.current = localDecisions;

  const getStatus = useCallback(
    (proposalId: string): ScoutProposalResponseStatus | undefined => {
      return handlers?.respondedProposals[proposalId] ?? localDecisions[proposalId]?.status;
    },
    [handlers, localDecisions]
  );

  const pendingProposals = useMemo(() => {
    return proposals.filter((proposal) => getStatus(proposal.proposalId) === undefined);
  }, [getStatus, proposals]);

  const hasHandlers = handlers !== undefined;
  const isReadOnly = surfaceReadOnly || !hasHandlers;
  const isSubmitting = submittingAction !== null || (handlers?.isProcessing ?? false);
  const showStackActions = !isReadOnly && proposals.length > 1 && pendingProposals.length > 0;
  // Keep Accept/Reject locked while the agent turn is still streaming so a
  // multi-chunk batch cannot be resolved before every card has been staged.
  const isLockedOut = isSubmitting || !hasHandlers || turnActionsDisabled;

  const flushResolutions = useCallback(
    async (items: ResolveItem[]) => {
      if (!handlers || items.length === 0) return;
      setHasError(false);
      setSubmittingAction("flush");
      try {
        await handlers.onResolve({ proposals: items });
        setLocalDecisions((current) => {
          const next = { ...current };
          for (const item of items) {
            delete next[item.proposalId];
          }
          localDecisionsRef.current = next;
          return next;
        });
      } catch (error) {
        setHasError(true);
        throw error;
      } finally {
        setSubmittingAction(null);
      }
    },
    [handlers]
  );

  const buildResolveItem = useCallback(
    (
      proposal: ScoutDataChangeProposal,
      status: ScoutProposalResponseStatus,
      title?: string
    ): ResolveItem => {
      const resolvedTitle = title ?? formatDataChangeProposalTitle(proposal);
      if (status === "confirmed") {
        return {
          proposalId: proposal.proposalId,
          title: resolvedTitle,
          status,
          operation: proposal.operation,
        };
      }
      return {
        proposalId: proposal.proposalId,
        title: resolvedTitle,
        status,
      };
    },
    []
  );

  const decideOne = useCallback(
    async (proposal: ScoutDataChangeProposal, status: ScoutProposalResponseStatus) => {
      if (!handlers || isSubmitting || turnActionsDisabled) return;

      const title = formatDataChangeProposalTitle(proposal);
      const decision: LocalDecision = {
        status,
        title,
        ...(status === "confirmed" ? { operation: proposal.operation } : {}),
      };

      const nextLocal: Record<string, LocalDecision> = {
        ...localDecisionsRef.current,
        [proposal.proposalId]: decision,
      };
      localDecisionsRef.current = nextLocal;
      setLocalDecisions(nextLocal);

      const remainingPending = proposals.filter((candidate) => {
        if (handlers.respondedProposals[candidate.proposalId]) return false;
        return nextLocal[candidate.proposalId] === undefined;
      });

      // Sibling cards still open — park this decision locally; do not send yet.
      if (remainingPending.length > 0) {
        return;
      }

      const toFlush: ResolveItem[] = [];
      for (const candidate of proposals) {
        if (handlers.respondedProposals[candidate.proposalId]) continue;
        const local = nextLocal[candidate.proposalId];
        if (!local) continue;
        toFlush.push(buildResolveItem(candidate, local.status, local.title));
      }
      await flushResolutions(toFlush);
    },
    [buildResolveItem, flushResolutions, handlers, isSubmitting, proposals, turnActionsDisabled]
  );

  const handleAcceptAll = async () => {
    if (!handlers || isSubmitting || turnActionsDisabled || pendingProposals.length === 0) return;
    setHasError(false);
    setSubmittingAction("confirm-all");

    const priorLocal = localDecisionsRef.current;
    const nextLocal: Record<string, LocalDecision> = { ...priorLocal };
    for (const proposal of pendingProposals) {
      nextLocal[proposal.proposalId] = {
        status: "confirmed",
        title: formatDataChangeProposalTitle(proposal),
        operation: proposal.operation,
      };
    }
    localDecisionsRef.current = nextLocal;
    setLocalDecisions(nextLocal);

    try {
      const toFlush: ResolveItem[] = [];
      for (const candidate of proposals) {
        if (handlers.respondedProposals[candidate.proposalId]) continue;
        const local = nextLocal[candidate.proposalId];
        if (!local) continue;
        toFlush.push(buildResolveItem(candidate, local.status, local.title));
      }
      await handlers.onResolve({ proposals: toFlush });
      setLocalDecisions((current) => {
        const next = { ...current };
        for (const item of toFlush) {
          delete next[item.proposalId];
        }
        localDecisionsRef.current = next;
        return next;
      });
    } catch {
      setHasError(true);
      localDecisionsRef.current = priorLocal;
      setLocalDecisions(priorLocal);
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleRejectAll = async () => {
    if (!handlers || isSubmitting || turnActionsDisabled || pendingProposals.length === 0) return;
    setHasError(false);
    setSubmittingAction("decline-all");

    const priorLocal = localDecisionsRef.current;
    const nextLocal: Record<string, LocalDecision> = { ...priorLocal };
    for (const proposal of pendingProposals) {
      nextLocal[proposal.proposalId] = {
        status: "declined",
        title: formatDataChangeProposalTitle(proposal),
      };
    }
    localDecisionsRef.current = nextLocal;
    setLocalDecisions(nextLocal);

    try {
      const toFlush: ResolveItem[] = [];
      for (const candidate of proposals) {
        if (handlers.respondedProposals[candidate.proposalId]) continue;
        const local = nextLocal[candidate.proposalId];
        if (!local) continue;
        toFlush.push(buildResolveItem(candidate, local.status, local.title));
      }
      await handlers.onResolve({ proposals: toFlush });
      setLocalDecisions((current) => {
        const next = { ...current };
        for (const item of toFlush) {
          delete next[item.proposalId];
        }
        localDecisionsRef.current = next;
        return next;
      });
    } catch {
      setHasError(true);
      localDecisionsRef.current = priorLocal;
      setLocalDecisions(priorLocal);
    } finally {
      setSubmittingAction(null);
    }
  };

  if (proposals.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="scout-data-change-proposal-stack"
      sx={{
        mt: 1,
        maxWidth: 720,
      }}
    >
      <Stack spacing={1.25}>
        {showStackActions ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <Typography variant="body2" color="text.secondary">
              {pendingProposals.length} proposed{" "}
              {pendingProposals.length === 1 ? "change" : "changes"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={handleRejectAll}
                disabled={isLockedOut}
                startIcon={
                  submittingAction === "decline-all" ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Reject all
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleAcceptAll}
                disabled={isLockedOut}
                startIcon={
                  submittingAction === "confirm-all" ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : undefined
                }
              >
                Accept all
              </Button>
            </Stack>
          </Stack>
        ) : null}
        {hasError ? (
          <Alert severity="error">
            Something went wrong responding to these changes. Please try again.
          </Alert>
        ) : null}
        {proposals.map((proposal) => (
          <ScoutDataChangeProposalCard
            key={proposal.proposalId}
            proposal={proposal}
            responseStatus={getStatus(proposal.proposalId)}
            hideActions={isReadOnly}
            actionsDisabled={isSubmitting || turnActionsDisabled}
            onAccept={() => decideOne(proposal, "confirmed")}
            onReject={() => decideOne(proposal, "declined")}
          />
        ))}
      </Stack>
    </Box>
  );
}
